import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Pressable,
  Image,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import BackArrow from '../assets/svg/BackArrow';
import ChevronRight from '@assets/svg/ChevronRight';
import MaskgroupTop from "@assets/svg/Wallet/MaskgroupTop";
import MaskgroupBottom from "@assets/svg/Wallet/MaskgroupBottom";
import { ScaledSheet } from '@utils/scale';
import { getWalletTransactions } from "@services/api";
import WeeklyDatePicker from "./WeeklyDatePicker";

const WalletTypeView = ({ type, date_range = {}, route }) => {
  const navigation = useNavigation();
  const [selectedDateRange, setSelectedDateRange] = useState(date_range);

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get screen dimensions for responsive design
  const { width: screenWidth } = Dimensions.get('window');
  
  // Calculate responsive sizes (based on 375px design width)
  const scale = screenWidth / 375;
  const maskTopWidth = Math.round(144 * scale);
  const maskTopHeight = Math.round(101 * scale);
  const maskBottomWidth = Math.round(102 * scale);
  const maskBottomHeight = Math.round(138 * scale);

  useFocusEffect(
    useCallback(() => {
      if (type && selectedDateRange?.key) {
        fetchData(type, selectedDateRange);
      } else if (type) {
        fetchData(type);
      }
    }, [])
  )

  const fetchData = async (type, date_range = {}) => {
    try {
      setIsLoading(true);
      const { data } = await getWalletTransactions(type, date_range?.key || '');
      console.log('data', data)
      setData(data);
      setSelectedDateRange(data?.selected_filter);
    } catch (error) {
      console.log(error);
      console.log(error.response.data);
    } finally {
      setIsLoading(false);
    }
  }

  // Configuration based on type
  const config = {
    'Incentives': {
      title: 'Incentive',
      headerBgStart: '#2CA56F',
      headerBgEnd: '#2CA56F',
      amountColor: '#000000',
      transactionAmountColor: '#2CA56F',
    },
    DEDUCTIONS: {
      title: 'Deductions',
      headerBgStart: '#F37500',
      headerBgEnd: '#F37500',
      amountColor: '#000000',
      transactionAmountColor: '#FF5722',
    },
    WITHDRAWAL: {
      title: 'Withdrawals',
      headerBgStart: '#F97316',
      headerBgEnd: '#EA580C',
      amountColor: '#000000',
      transactionAmountColor: '#EF4444',
    },
  };

  const currentConfig = config[type] || config.Incentives;

  const handleDateRangeChange = (dateRange) => {
    setSelectedDateRange(dateRange);
    fetchData(type, dateRange);
  };

  return (
    <View style={styles.container}>
      {/* Header with gradient background */}
      <View
        style={[
          styles.header,
          {
            backgroundColor: currentConfig.headerBgStart,
          },
        ]}
      >
        {/* Background circles in top right */}
        <View style={styles.headerBackgroundContainer}>
          <View style={styles.maskgroupTop}>
            <MaskgroupTop width={maskTopWidth} height={maskTopHeight} />
          </View>
          <View style={styles.maskgroupBottom}>
            <MaskgroupBottom width={maskBottomWidth} height={maskBottomHeight} />
          </View>
        </View>
        
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <BackArrow color="#FFFFFF" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{data?.title || type}</Text>
        </View>
        <View style={styles.amountCard}>

          <WeeklyDatePicker
            dateRangeOptions={data?.date_filter_options || []}
            dateRangeText={selectedDateRange?.value}
            selectedDate={selectedDateRange}
            onDateChange={handleDateRangeChange}
          />

          {/* Total Amount */}
          <Text style={[styles.totalAmount, { color: currentConfig.amountColor }]}>
            {data?.summary?.value_txt}
          </Text>
        </View>
      </View>

      {isLoading ? 
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color="#000" />
        </View>
      : <FlatList
          data={data?.transactions}
          keyExtractor={(item, index) => {
            // Some API responses omit id, so fall back to other unique-ish fields
            const candidateId = item?.id ?? item?.transaction_id ?? item?.key;
            return candidateId !== undefined && candidateId !== null
              ? String(candidateId)
              : `transaction-${index}`;
          }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[
            styles.flatListContent,
            {
              backgroundColor: currentConfig.headerBgStart,
            },
          ]}
          renderItem={({ item, index }) => {
            const isLast = index === data?.transactions?.length - 1;
            const isNavigable = item?.nav_enabled && Array.isArray(item?.txn_ids);

            const handlePress = () => {
              if (!isNavigable) return;
              navigation.navigate("GroupedTxnsScreen", {
                txn_ids: item.txn_ids,
              });
            };

            return (
              <Pressable
                onPress={handlePress}
                disabled={!isNavigable}
                style={({ pressed }) => [
                  styles.transactionItem,
                  index === 0 && styles.firstTransactionItem,
                  pressed && isNavigable && styles.transactionItemPressed,
                ]}
              >
                <View style={styles.transactionContent}>
                  <View style={styles.transactionRow}>
                    <Text style={styles.transactionTitle}>{item.title}</Text>
                <View style={styles.amountRow}>
                  <Text
                    style={[
                      styles.transactionAmount,
                      { color: currentConfig.transactionAmountColor },
                    ]}
                  >
                    {item.value_txt}
                  </Text>
                  {isNavigable && (
                    <ChevronRight width={14} height={14} pathFill="#9CA3AF" />
                  )}
                </View>
                  </View>
                  <Text style={styles.transactionTime}>{item.timestamp_txt}</Text>
                  {isNavigable && Array.isArray(item?.subtitle_lines) && (
                    <View style={styles.subtitleContainer}>
                      {item.subtitle_lines.map((line, idx) => (
                        <Text key={idx} style={styles.subtitleText}>
                          {line}
                        </Text>
                      ))}
                    </View>
                  )}
                </View>
                {!isLast && <View style={styles.separator} />}
              </Pressable>
            );
          }}
          ListFooterComponent={<View style={styles.footer} />}
        />}
        </View>
  )
};

const styles = ScaledSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  header: {
    paddingTop: 12,
    paddingBottom: 24,
    overflow: 'hidden',
    position: 'relative',
  },
  headerBackgroundContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 0,
  },
  maskgroupTop: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  maskgroupBottom: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingTop: 8,
    marginBottom: 20,
    zIndex: 1,
  },
  backButton: {
    padding: 8,
    marginRight: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 0,
  },
  flatListContent: {
  },
  amountCard: {
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
    borderRadius: 20,
    paddingTop: 16,
    paddingBottom: 20,
    paddingHorizontal: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  dateRangeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: 20,
  },
  dateArrow: {
    paddingHorizontal: 8,
  },
  dateArrowText: {
    fontSize: 24,
    color: '#4A5565',
    fontWeight: '600',
  },
  dateRangeTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 12,
  },
  dateRangeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E8E9EA',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 8,
    marginHorizontal: 8,
    gap: 6,
  },
  dropdownIcon: {
    paddingLeft: 8
  },
  dateRangeText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
    letterSpacing: -0.15,
    color: '#0A0A0A',
  },
  dropdownIcon: {
    fontSize: 10,
    color: '#6B7280',
    transform: [{ rotate: '90deg' }],
  },
  totalAmount: {
    fontSize: 48,
    fontWeight: '700',
    textAlign: 'center',
    letterSpacing: -0.5,
    lineHeight: 56,
    marginTop: 4,
    marginBottom: 8,
  },
  transactionList: {
    marginTop: 24,
    paddingHorizontal: 16,
  },
  transactionItem: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    backgroundColor: '#FFFFFF',
    position: 'relative',
    borderRadius: 0,
  },
  firstTransactionItem: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  transactionItemPressed: {
    opacity: 0.8,
  },
  separator: {
    position: 'absolute',
    bottom: 0,
    left: 16,
    right: 16,
    height: 1,
    backgroundColor: '#F3F4F6',
  },
  transactionContent: {
    flex: 1,
  },
  transactionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  transactionTitle: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    letterSpacing: -0.31,
    color: '#000000',
    fontStyle: 'regular',
    flex: 1,
  },
  transactionTime: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    letterSpacing: -0.15,
    color: '#4A5565',
    fontStyle: 'regular',
  },
  transactionAmount: {
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 24,
    letterSpacing: -0.31,
    marginLeft: 16,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
  },
  subtitleContainer: {
    marginTop: 4,
  },
  subtitleText: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  footer: {
    backgroundColor: '#FFFFFF',
    height: 100
  }
});

export default WalletTypeView;
